import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type FormEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import MainAdsLayout from "@/_component/MainAdsLayout";
import {
    Button,
    CheckboxInput,
    InputLabel,
    PageTitle,
    RadioInput,
    TextInput,
} from "@/_component/Common";
import type { HandleInputChange } from "@/_component/Common/form/fields/types";
import AddressForm, {
    addressValidationSchema,
    createEmptyAddressValues,
    type AddressErrors,
    type AddressFormValues,
} from "@/_component/shared/common/address/AddressForm";
import {
    addOrGetOnboardingUserAccess,
    fetchCategoryBySlug,
    saveOnboardingAddress,
    updateOnboardingUser,
} from "@/apis";
import { TERMCONDITION } from "@/_constant";
import { useAuthContext, useLanguageContext } from "@/hooks";
import {
    setPrimaryAddress,
    upsertUserAccess,
    useAddressStore,
} from "@/store/slices";
import { fetchAddress } from "@/store/thunks";
import { Error, Success } from "@/_service/notification/notification";
import {
    constructValidationError,
    renderError,
    timeout,
    validateSchema,
} from "@/utility";

export type OnboardingExtraFieldName = "organizationName" | "gstNumber";

export type OnboardingExtraField = {
    name: OnboardingExtraFieldName;
    labelKey: string;
    label: string;
    required?: boolean;
};

export type AlternateAccessType = {
    accessType: OnboardingAccessType;
    labelKey: string;
    label: string;
    route: string;
};

export type OnboardingAccessType =
    | "customer"
    | "dealer"
    | "service-provider"
    | "business-developer"
    | "investor";

export type OnboardingDetailsConfig = {
    accessType: OnboardingAccessType;
    accessId: number;
    categoryId?: string;
    categoryKey?: string;
    titleKey: string;
    title: string;
    extraFields: OnboardingExtraField[];
    alternateAccessTypes: AlternateAccessType[];
    getSuccessPath: (context: {
        categoryKey?: string;
        categoryId: string;
        userAccessId: string;
        addressId: string;
    }) => string;
};

type FormValues = {
    name: string;
    phone: string;
    email: string;
    organizationName: string;
    gstNumber: string;
    agreeTerms: boolean;
};

type RegistrationErrors = Record<string, string>;

type OnboardingMetadata = {
    accessKey: "GST" | "Organisation_Name";
    accessValue: string;
};

const mapApiAddressToForm = (address: Record<string, any>): AddressFormValues => ({
    id: address.id ? String(address.id) : "",
    name: address.name ?? "",
    phoneNumber: address.phoneNumber ?? "",
    villageCity: address.villageCity ?? "",
    houseNoStreet: address.houseNoStreet ?? "",
    tehsil: address.tehsil ?? "",
    district: address.district ?? "",
    pincode: address.pincode ?? "",
    state: address.state ?? "",
    country: address.country ?? "",
    email: address.email ?? "",
    isPrimary: Boolean(address.isPrimary),
});

const getResponseId = (response: unknown): string => {
    if (typeof response === "string" || typeof response === "number") {
        return String(response);
    }
    if (response && typeof response === "object" && "id" in response) {
        const id = (response as { id?: string | number }).id;
        return id === undefined || id === null ? "" : String(id);
    }
    return "";
};

const createValidationSchema = (extraFields: OnboardingExtraField[]) => {
    const isRequired = (fieldName: OnboardingExtraFieldName) =>
        extraFields.some((field) => field.name === fieldName && field.required);

    return Yup.object({
        name: Yup.string().trim().required("Name is required"),
        phone: Yup.string().trim().required("Phone number is required"),
        email: Yup.string()
            .trim()
            .email("Enter a valid email")
            .required("Email is required"),
        organizationName: isRequired("organizationName")
            ? Yup.string().trim().required("Name of organization is required")
            : Yup.string().trim(),
        gstNumber: isRequired("gstNumber")
            ? Yup.string().trim().required("GST number is required")
            : Yup.string().trim(),
        agreeTerms: Yup.boolean().oneOf(
            [true],
            "Please accept the terms & conditions"
        ),
        address: addressValidationSchema,
    });
};

const OnboardingDetailsPage = ({ config }: { config: OnboardingDetailsConfig }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { language } = useLanguageContext();
    const { user } = useAuthContext();
    const {
        data: addressList,
        primaryAddress: storedPrimaryAddress,
    } = useAddressStore();

    const [formValues, setFormValues] = useState<FormValues>({
        name: "",
        phone: "",
        email: "",
        organizationName: "",
        gstNumber: "",
        agreeTerms: false,
    });
    const [customAddress, setCustomAddress] = useState<AddressFormValues>(
        createEmptyAddressValues()
    );
    const [usePrimaryAddress, setUsePrimaryAddress] = useState(() =>
        Boolean(
            storedPrimaryAddress?.isPrimary ||
                addressList.some(
                    (address: Record<string, any>) =>
                        address.isPrimary === true
                )
        )
    );
    const [isLoadingAddresses, setLoadingAddresses] = useState(
        addressList.length === 0
    );
    const [resolvedCategoryId, setResolvedCategoryId] = useState(
        config.categoryId ?? ""
    );
    const [isResolvingCategory, setResolvingCategory] = useState(
        Boolean(config.categoryKey && !config.categoryId)
    );
    const [errors, setErrors] = useState<RegistrationErrors | null>(null);
    const [isSubmitting, setSubmitting] = useState(false);
    const addressRequestRef = useRef<
        Promise<Record<string, any>[]> | null
    >(null);

    const validationSchema = useMemo(
        () => createValidationSchema(config.extraFields),
        [config.extraFields]
    );
    const primaryAddress = useMemo(() => {
        const address = storedPrimaryAddress?.isPrimary
            ? storedPrimaryAddress
            : addressList.find((item: Record<string, any>) => item.isPrimary);
        return address ? mapApiAddressToForm(address) : null;
    }, [addressList, storedPrimaryAddress]);

    useEffect(() => {
        setResolvedCategoryId(config.categoryId ?? "");
        if (!config.categoryKey || config.categoryId) {
            setResolvingCategory(false);
            return;
        }

        let active = true;
        setResolvingCategory(true);
        fetchCategoryBySlug(config.categoryKey)
            .then((response: any) => {
                if (!active) return;
                const rawCategory = response?.data?.response;
                const category = Array.isArray(rawCategory)
                    ? rawCategory[0]
                    : rawCategory;
                const categoryId = category?.id ?? category?.categoryId;
                if (categoryId === undefined || categoryId === null) {
                    throw new globalThis.Error("Category not found");
                }
                const categoryAccessId =
                    category?.accessId ?? category?.access_id;
                if (
                    categoryAccessId !== undefined &&
                    Number(categoryAccessId) !== config.accessId
                ) {
                    throw new globalThis.Error(
                        "Category does not belong to this access type"
                    );
                }
                setResolvedCategoryId(String(categoryId));
            })
            .catch(() => {
                if (!active) return;
                setResolvedCategoryId("");
                Error("Unable to load the selected category.");
            })
            .finally(() => {
                if (active) setResolvingCategory(false);
            });

        return () => {
            active = false;
        };
    }, [config.accessId, config.categoryId, config.categoryKey]);

    useEffect(() => {
        if (!user) return;
        setFormValues((current) => ({
            ...current,
            name: user.name || current.name,
            phone: user.phone_number || user.phoneNumber || current.phone,
            email: user.email || current.email,
            organizationName: user.org_name || current.organizationName,
            gstNumber: user.gst_no || current.gstNumber,
        }));
    }, [user]);

    useEffect(() => {
        setCustomAddress((current) => ({
            ...current,
            name: formValues.name || current.name,
            phoneNumber: formValues.phone || current.phoneNumber,
            email: formValues.email || current.email,
        }));
    }, [formValues.email, formValues.name, formValues.phone]);

    useEffect(() => {
        if (addressList.length > 0) {
            const primary = addressList.find(
                (address: Record<string, any>) => address.isPrimary === true
            );
            dispatch(setPrimaryAddress(primary));
            setUsePrimaryAddress(Boolean(primary));
            setLoadingAddresses(false);
            return;
        }

        let active = true;
        setLoadingAddresses(true);
        if (!addressRequestRef.current) {
            addressRequestRef.current = dispatch(fetchAddress() as any).unwrap();
        }

        addressRequestRef.current && addressRequestRef.current
            .then((addresses: Record<string, any>[]) => {
                if (!active) return;
                const primary = addresses.find(
                    (address) => address.isPrimary === true
                );
                dispatch(setPrimaryAddress(primary));
                setUsePrimaryAddress(Boolean(primary));
            })
            .catch(() => {
                if (active) setUsePrimaryAddress(false);
            })
            .finally(() => {
                if (active) setLoadingAddresses(false);
            });

        return () => {
            active = false;
        };
    }, [addressList, dispatch]);

    const handleInputChange: HandleInputChange = (_event, fieldName, value) => {
        setFormValues((current) => ({
            ...current,
            [fieldName]: String(value ?? ""),
        }));
    };

    const handleCheckboxChange: HandleInputChange = (
        _event,
        fieldName,
        value
    ) => {
        setFormValues((current) => ({
            ...current,
            [fieldName]: Boolean(value),
        }));
    };

    const handleAddressOptionChange = (isPrimary: boolean) => {
        if (isPrimary && !primaryAddress) {
            Error("Primary address not found. Please add a new address.");
            return;
        }
        setUsePrimaryAddress(isPrimary);
        setErrors(null);
    };

    const handleCustomAddressChange = (
        field: keyof AddressFormValues,
        value: string
    ) => {
        if (usePrimaryAddress) return;
        setCustomAddress((current) => {
            const next = { ...current, [field]: value };
            if (field === "country") {
                next.state = "";
                next.district = "";
                next.tehsil = "";
            } else if (field === "state") {
                next.district = "";
                next.tehsil = "";
            } else if (field === "district") {
                next.tehsil = "";
            }
            return next;
        });
    };

    const getNormalizedAddress = () => {
        const address = usePrimaryAddress
            ? primaryAddress ?? createEmptyAddressValues()
            : customAddress;
        return {
            ...address,
            name: formValues.name || address.name,
            phoneNumber: formValues.phone || address.phoneNumber,
            email: formValues.email || address.email,
        };
    };

    const validateRegistration = (): AddressFormValues | null => {
        const address = getNormalizedAddress();
        try {
            validateSchema(validationSchema, { ...formValues, address });
            setErrors(null);
            return address;
        } catch (error) {
            const validationErrors = constructValidationError(
                error
            ) as RegistrationErrors;
            setErrors(validationErrors);
            timeout(() => setErrors(null), 5000);
            return null;
        }
    };

    const buildMetadata = (): OnboardingMetadata[] => {
        const configuredFields = new Set(
            config.extraFields.map((field) => field.name)
        );
        const metadata: OnboardingMetadata[] = [];
        if (configuredFields.has("gstNumber")) {
            metadata.push({ accessKey: "GST", accessValue: formValues.gstNumber });
        }
        if (configuredFields.has("organizationName")) {
            metadata.push({
                accessKey: "Organisation_Name",
                accessValue: formValues.organizationName,
            });
        }
        return metadata;
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmitting || isResolvingCategory || isLoadingAddresses) return;
        if (!resolvedCategoryId) {
            Error("A valid category is required to continue.");
            return;
        }

        const address = validateRegistration();
        if (!address) return;

        setSubmitting(true);
        try {
            await updateOnboardingUser({
                access: config.accessId,
                email: formValues.email,
                enabled: true,
                gst_no: formValues.gstNumber,
                name: formValues.name,
                org_name: formValues.organizationName,
                phoneNumber: formValues.phone,
                userId: "",
            });

            let addressId = address.id ?? "";
            if (!usePrimaryAddress || !addressId) {
                const addressPayload = {
                    country: address.country,
                    detectedLocation: "",
                    district: address.district,
                    email: formValues.email,
                    houseNoStreet: address.houseNoStreet,
                    id: address.id ?? "",
                    isPrimary: false,
                    name: formValues.name,
                    new: true,
                    parentUserId: "",
                    phoneNumber: formValues.phone,
                    phoneNumberVerified: true,
                    pincode: address.pincode,
                    state: address.state,
                    tehsil: address.tehsil,
                    userId: "",
                    villageCity: address.villageCity,
                };
                const addressResponse = await saveOnboardingAddress(
                    addressPayload
                );
                addressId = getResponseId(addressResponse?.data?.response);
                try {
                    const refreshedAddresses = await dispatch(
                        fetchAddress() as any
                    ).unwrap();
                    const refreshedPrimary = refreshedAddresses.find(
                        (item: Record<string, any>) =>
                            item.isPrimary === true
                    );
                    dispatch(setPrimaryAddress(refreshedPrimary));
                } catch {
                    // Address creation succeeded; a later address view can retry.
                }
            }

            if (!addressId) {
                Error("Address was not saved. Please try again.");
                return;
            }

            const accessResponse = await addOrGetOnboardingUserAccess({
                accessId: String(config.accessId),
                categoryId: resolvedCategoryId,
                addressId,
                metadata: buildMetadata(),
            });
            const userAccessId = getResponseId(accessResponse?.data?.response);
            if (!userAccessId) {
                Error("Unable to configure user access.");
                return;
            }

            const accessData = accessResponse?.data?.response;
            dispatch(
                upsertUserAccess(
                    accessData && typeof accessData === "object"
                        ? {
                              ...accessData,
                              id: (accessData as any).id ?? userAccessId,
                              accessId:
                                  (accessData as any).accessId ?? config.accessId,
                              category_id:
                                  (accessData as any).category_id ??
                                  (accessData as any).categoryId ??
                                  resolvedCategoryId,
                              addressId:
                                  (accessData as any).addressId ?? addressId,
                          }
                        : {
                              id: userAccessId,
                              accessId: config.accessId,
                              category_id: resolvedCategoryId,
                              addressId,
                          }
                )
            );

            Success("Registration completed successfully.");
            navigate(
                config.getSuccessPath({
                    categoryKey: config.categoryKey,
                    categoryId: resolvedCategoryId,
                    userAccessId,
                    addressId,
                }),
                { replace: true }
            );
        } catch (error: any) {
            Error(
                error?.response?.data?.message ||
                    error?.message ||
                    "Registration failed. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const addressValues = usePrimaryAddress
        ? primaryAddress ?? createEmptyAddressValues()
        : customAddress;
    const addressErrors = useMemo(() => {
        if (usePrimaryAddress || !errors) return {};
        return Object.entries(errors).reduce<AddressErrors>(
            (result, [field, message]) => {
                if (!field.startsWith("address.")) return result;
                result[
                    field.replace("address.", "") as keyof AddressFormValues
                ] = message;
                return result;
            },
            {}
        );
    }, [errors, usePrimaryAddress]);

    return (
        <MainAdsLayout noSpacing>
            <div className="pt-5">
                <div className="px-5">
                    <PageTitle
                        headingPart1={language("label.join", "Join")}
                        headingPart2={language(config.titleKey, config.title)}
                    />
                </div>
                <form className="mt-4" onSubmit={handleSubmit}>
                    <div className="px-5">
                        <div className="row g-4">
                            <div className="col-lg-6">
                                <InputLabel
                                    required
                                    label={language("label.name", "Name")}
                                />
                                <TextInput
                                    name="name"
                                    value={formValues.name}
                                    onChange={handleInputChange}
                                    disabled
                                />
                                {renderError(errors, "name")}
                            </div>
                            <div className="col-lg-6">
                                <InputLabel
                                    required
                                    label={language(
                                        "label.phone_number",
                                        "Phone"
                                    )}
                                />
                                <TextInput
                                    name="phone"
                                    value={formValues.phone}
                                    onChange={handleInputChange}
                                    disabled
                                />
                                {renderError(errors, "phone")}
                            </div>
                            <div className="col-lg-6">
                                <InputLabel
                                    required
                                    label={language("label.email", "Email")}
                                />
                                <TextInput
                                    name="email"
                                    value={formValues.email}
                                    onChange={handleInputChange}
                                />
                                {renderError(errors, "email")}
                            </div>
                            {config.extraFields.map((field) => (
                                <div className="col-lg-6" key={field.name}>
                                    <InputLabel
                                        required={field.required}
                                        label={language(
                                            field.labelKey,
                                            field.label
                                        )}
                                    />
                                    <TextInput
                                        name={field.name}
                                        value={formValues[field.name]}
                                        onChange={handleInputChange}
                                    />
                                    {renderError(errors, field.name)}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-5 rounded-top-4 app-bg-secondary-light">
                        <div className="p-5">
                            <div className="d-flex flex-wrap gap-3 gap-lg-5 align-items-center mb-3">
                                <h5 className="mb-0">
                                    {language("label.address", "Address")}
                                </h5>
                                <RadioInput
                                    name="addressOption"
                                    value={
                                        usePrimaryAddress ? "primary" : "custom"
                                    }
                                    options={[
                                        {
                                            label: language(
                                                "label.use_primary_address",
                                                "Use primary address"
                                            ),
                                            value: "primary",
                                            disabled: !primaryAddress,
                                        },
                                        {
                                            label: language(
                                                "label.use_new_address",
                                                "Use a different address"
                                            ),
                                            value: "custom",
                                        },
                                    ]}
                                    onChange={(_event, _name, value) =>
                                        handleAddressOptionChange(
                                            value === "primary"
                                        )
                                    }
                                />
                            </div>

                            <AddressForm
                                values={addressValues}
                                onChange={handleCustomAddressChange}
                                errors={addressErrors}
                                showContactFields={false}
                                disabled={usePrimaryAddress}
                            />

                            <div className="d-flex align-items-center mt-4">
                                <CheckboxInput
                                    name="agreeTerms"
                                    options={[
                                        {
                                            label: language(
                                                "label.i_agree_terms",
                                                "I agree to the"
                                            ),
                                            value: true,
                                        },
                                    ]}
                                    formState={formValues}
                                    onChange={handleCheckboxChange}
                                />
                                <Link
                                    to={TERMCONDITION}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {language(
                                        "label.terms_conditions",
                                        "terms & conditions"
                                    )}
                                </Link>
                            </div>
                            {renderError(errors, "agreeTerms")}

                            <div className="text-center mt-4">
                                <Button
                                    primary
                                    solid
                                    rounded
                                    type="submit"
                                    isLoading={isSubmitting}
                                    disabled={
                                        isResolvingCategory ||
                                        isLoadingAddresses
                                    }
                                >
                                    {language(
                                        "label.register_now",
                                        "Register Now"
                                    )}{" "}
                                    <i className="bi bi-chevron-right ms-2" />
                                </Button>
                            </div>
                        </div>

                        <div className="py-5 kg-bg-warning rounded-top-4">
                            <p className="text-center kg-white mb-4 fs-5 fw-bold">
                                {`OR ${language(
                                    "label.register_as",
                                    "register as"
                                )}`}
                            </p>
                            <div className="d-flex flex-wrap gap-3 justify-content-center justify-content-lg-around px-5 align-items-center">
                                {config.alternateAccessTypes.map((access) => (
                                    <Button
                                        key={access.accessType}
                                        white
                                        outline
                                        rounded
                                        transparent
                                        onClick={() => navigate(access.route)}
                                    >
                                        {language(access.labelKey, access.label)}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </MainAdsLayout>
    );
};

export default OnboardingDetailsPage;
